import styled from 'styled-components';
import { fadeInOnly } from '../../utils/animations';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Email from './email';
import Map from './map';
import { useTranslation } from 'react-i18next';
import React from 'react';

const ContactPageContainer = styled.div` /* Renamed */
  width: 100%;
  padding: 2rem; /* Added page padding */
  background-color: ${(props) => props.theme.colors.background}; /* Updated */
  color: ${(props) => props.theme.colors.text}; /* Updated */
  display: flex;
  flex-direction: column;
  align-items: center; /* Center sections like Email and Map */
  opacity: 0; /* Initial state for page load animation */
  animation: ${fadeInOnly} 0.5s ease-out forwards; /* Page load animation */
`;

const InfoContainer = styled.div`
  display: flex;
  flex-wrap: wrap; /* Allow wrapping for cards */
  justify-content: center; /* Center cards */
  gap: 2rem; /* Spacing between cards */
  padding: 2rem 0; /* Vertical padding */
  width: 100%; /* Take full width for card centering */
`;

const Title = styled.h3`
  font-family: ${(props) => props.theme.fontFamilies.primary}; /* Updated */
  font-size: ${(props) => props.theme.fontSizes.h3}; /* Updated */
  color: ${(props) => props.theme.colors.primary}; /* Updated */
  line-height: ${(props) => props.theme.lineHeights.heading}; /* Updated */
  font-weight: 600;
  margin: 0 0 0.75rem 0; /* Adjusted margin */
  text-align: center; 
`;

const SectionTitle = styled.h2` /* Was TitleBlue */
  font-family: ${(props) => props.theme.fontFamilies.primary}; /* Updated */
  font-size: ${(props) => props.theme.fontSizes.h2}; /* Updated */
  color: ${(props) => props.theme.colors.primary}; /* Updated */
  line-height: ${(props) => props.theme.lineHeights.heading}; /* Updated */
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.2rem; /* Slightly reduced */
  margin-top: 3rem; /* Adjusted margin */
  margin-bottom: 1.5rem; /* Added margin */
`;

const Description = styled.p`
  font-family: ${(props) => props.theme.fontFamilies.secondary}; /* Updated */
  font-size: ${(props) => props.theme.fontSizes.body}; /* Updated */
  color: ${(props) => props.theme.colors.text}; /* Updated */
  line-height: ${(props) => props.theme.lineHeights.body}; /* Updated */
  margin: 0; /* Reset margin */
  text-align: center;
  white-space: pre-wrap; 
`;

const ContactContainer = styled.div`
  flex: 1 1 300px; /* Flex properties for responsiveness */
  max-width: 350px; /* Max width for each card */
  min-height: 220px; /* Adjusted min-height */
  background-color: ${(props) => props.theme.colors.surface}; /* Updated */
  display: flex;
  /* margin: 1rem; Removed, using gap on parent */
  flex-direction: column;
  border-radius: ${(props) => props.theme.borderRadius}; /* Updated */
  justify-content: center;
  align-items: center; 
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); /* Updated shadow */
  transition: transform 0.3s ease-out, box-shadow 0.3s ease-out; /* Updated transition */
  animation: ${fadeInOnly} 0.9s ease-in-out;
  animation-fill-mode: both;
  padding: 1.5rem; /* Adjusted padding */
  @media (max-width: 480px) {
    padding: 1rem;
  }

  &:hover {
    transform: translateY(-4px); /* Updated hover effect */
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12); /* Updated hover shadow */
  }
`;

const IconContainer = styled.div`
  height: 60px; /* Slightly smaller */
  width: 60px;  /* Slightly smaller */
  background-color: ${(props) => props.theme.colors.primary}; /* Updated */
  color: ${(props) => props.theme.colors.surface}; /* Updated for icon color (assuming surface is light) */
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  margin-bottom: 1rem; 
`;

const MapContainer = styled.div`
  height: 400px; /* Give map a fixed height */
  width: 100%;
  max-width: 900px; /* Max width for map container */
  margin-top: 3rem; /* Spacing above map */
  border-radius: ${(props) => props.theme.borderRadius};
  overflow: hidden; /* To ensure map corners are rounded */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

// iconStyle remains the same as it's for FontAwesomeIcon size, color is handled by IconContainer

// No explicit props
interface ContactProps {}

const ContactComponent: React.FC<ContactProps> = () => {
  const { t } = useTranslation();
  return (
    <ContactPageContainer>
      <InfoContainer>
        <ContactContainer>
          <IconContainer>
            <FontAwesomeIcon icon="location-pin" />
          </IconContainer>
          <Title>{t('contact.addressTitle')}</Title>
          <Description>{t('contact.addressDetail')}</Description>
        </ContactContainer>
        <ContactContainer>
          <IconContainer>
            <FontAwesomeIcon icon="phone"  />
          </IconContainer>
          <Title>{t('contact.phoneTitle')}</Title>
          <Description>{t('contact.phoneDetail')}</Description>
        </ContactContainer>
        <ContactContainer>
          <IconContainer>
            <FontAwesomeIcon icon="clock"  />
          </IconContainer>
          <Title>{t('contact.hoursTitle')}</Title>
          <Description>{t('contact.hoursDetail')}</Description>
        </ContactContainer>
      </InfoContainer>
      
      <SectionTitle>{t('contact.emailUsTitle')}</SectionTitle> {/* Changed from TitleBlue */}
      <Email />

      <MapContainer>
        <Map />
      </MapContainer>
    </ContactPageContainer>
  );
}

export default React.memo(ContactComponent);
